import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import HeaderSidebar from "./Common/HeaderSidebar/HeaderSidebar";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { useUserDetails } from "@/context";

// Routes that render as a standalone document with no app sidebar/nav:
// the Active Buyer Report (own tab) and its public shared dead-end page.
const STANDALONE_ROUTE = /^\/(reports\/active-buyer|r)\//;

const DashboardShellContext = createContext({
  hasPersistentSidebar: false,
  registerSidebarProps: () => {},
});

export const useDashboardShell = () => useContext(DashboardShellContext);

const PersistentDashboardShell = ({ children }) => {
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const { isUserLoggedIn } = useUserDetails();
  const location = useLocation();
  const isStandalone = STANDALONE_ROUTE.test(location.pathname);

  const sidebarPropsRef = useRef({});
  const registerSidebarProps = useCallback((sidebarProps) => {
    sidebarPropsRef.current = sidebarProps || {};
  }, []);

  // NOTE: only the props listed here reach the persistent sidebar. Any new key
  // added to a page's headerSidebarProps must also be bridged here, or it will
  // be silently dropped on BLS routes (the sidebar lives above the router).
  const bridgedSidebarProps = useMemo(
    () => ({
      updateInventoryState: (...args) =>
        sidebarPropsRef.current.updateInventoryState?.(...args),
      onLoginSuccess: (...args) =>
        sidebarPropsRef.current.onLoginSuccess?.(...args),
    }),
    []
  );

  if (isGitchaDefaultWebsite) {
    return (
      <DashboardShellContext.Provider
        value={{ hasPersistentSidebar: false, registerSidebarProps }}
      >
        {children}
      </DashboardShellContext.Provider>
    );
  }

  const showSidebar = isUserLoggedIn && !isStandalone;

  return (
    <DashboardShellContext.Provider
      value={{ hasPersistentSidebar: showSidebar, registerSidebarProps }}
    >
      {showSidebar && <HeaderSidebar {...bridgedSidebarProps} />}
      {children}
    </DashboardShellContext.Provider>
  );
};

export default PersistentDashboardShell;
